import { computed } from 'vue';
import { toValue } from '../../vueuse/toValue';
import getBadgeInformation from '../../../utils/getBadgeInformation';
import { useDataStore } from '../../../composables/useVuexStore';
import getSelectedPriceData from '../../../utils/getSelectedPrice';
import getOffersData from '../../../utils/getOffersData';
import { PRICE } from '../../../store_modules/traitsStore/types';
import { perksPromotion, buildPerksPromotions } from '../../../utils/perksPromotion';

import { updateActiveBadges } from '../../../utils/updateActiveBadges';

function connectToStore() {
  return useDataStore(PRICE);
}

/**
 * Initializes the data in local "store".
 * This should only need to be called once on a component level.
 * @param {{ id: string, data: TraitPrice }} obj
 */
function useInitPrice({ id, data }) {
  const dataStore = connectToStore();
  if (!dataStore.has(id)) {
    dataStore.set(id, data);
  }
}
/**
 * Composable that provides reactive selectedPrice to useTraits
 * @param {object} param0
 * @param {import('@vueuse/core').MaybeRef<string>} param0.id
 * @param {SelectedColor} param0.selectedColor TODO: remove later and use useTraits
 * @param {SelectedSize} param0.selectedSize TODO: remove later and use useTraits
 * @param {{value: number}} param0.selectedUpc  TODO: remove later and use useUpc
 * @param {{value: UserData}} param0.userData
 * @param {UiKillswitchesMcom | {}} param0.uiKillswitches
 * @returns {{selectedPrice:object}}
 */
function usePrice({
  id, selectedColor, selectedSize, selectedUpc, userData, uiKillswitches = {},
}) {
  // TODO Post MVP: not all of the properties should be reactive
  // Some of the properties should be validated on client only once

  const rawData = connectToStore().getComputed(toValue(id));
  if (!rawData.value) {
    return {
      selectedPrice: {
        value: undefined,
      },
    };
  }

  const selectedUpcData = computed(
    () => rawData.value.relationships?.upcs?.[selectedUpc.value],
  );
  const selectedProductData = computed(() => {
    // non-consolidated product || non-latchkey consolidated product -> raw data
    if (!rawData.value.isConsolidatedMaster && !rawData.value.detail.flags.isConsolidatedThirdVariant) {
      return rawData.value;
    }
    if (rawData.value.isConsolidatedMaster && !selectedUpc.value) {
      // consolidated latchkey product && unselected upc -> latchkeyData
      return rawData.value.latchkeyData;
    }

    // consolidated product (latchkey) && upc selected  -> memberProduct
    const selectedProductId = selectedUpcData.value?.relationships?.selectedProduct ?? '';
    return rawData.value.relationships?.memberProductMap?.[selectedProductId] ?? {};
  });

  const colorPrice = computed(() => selectedColor.value?.pricing);
  const sizePrice = computed(() => selectedSize.value?.pricing);
  const upcPricing = computed(() => selectedUpcData.value?.pricing);

  const data = computed(() => {
    // consolidated pages (latchkey or non-latchkey) && upc selected -> upc
    const colorMap = rawData.value?.traits?.colors?.colorMap || {};
    const isOnlyOneColor = Object.keys(colorMap).length === 1;
    const isUpcPricingActivated = !!uiKillswitches?.upcPricingForMarketplaceStandardPDPEnabled
      && rawData.value.isMarketPlaceEligible && isOnlyOneColor;

    if (selectedUpc.value && (rawData.value.isConsolidatedPage || isUpcPricingActivated)) {
      return upcPricing.value;
    }
    // non-consolidated product || upc unselected
    return sizePrice.value || colorPrice.value || rawData.value.pricing;
  });

  const offers = computed(() => getBadgeInformation(selectedProductData.value, selectedSize.value?.id));

  const validatedOffers = computed(() => updateActiveBadges(offers.value, {
    tierName: userData?.value.tierName,
    shoppingMode: userData?.value.shoppingMode,
    user: userData?.value.user,
  }));

  const perksPromotions = computed(() => buildPerksPromotions(validatedOffers.value));

  const fauxOffer = computed(() => perksPromotion(offers.value));

  const finalPrice = computed(() => data.value?.price.finalPrice || {});

  const selectedColorOfferIds = computed(
    () => (rawData.value.traits?.colors?.colorMap && rawData.value.traits?.colors?.colorMap[selectedColor.value?.id]?.pricing
      ?.badgeIds) || [],
  );
  const offersData = computed(() => getOffersData({
    offers: validatedOffers.value,
    finalPrice: finalPrice.value,
    colorBadges: selectedColorOfferIds.value,
    flags: {
      isColorwayOffers: rawData.value.isColorwayOffers,
      isMasterProduct: rawData.value.isMasterProduct,
    },
  }));

  const isColorwayPrice = rawData.value?.traits?.traitsMaps?.priceToColors?.length > 1;
  const priceData = computed(() => getSelectedPriceData(data.value, offersData.value, { isColorwayPrice }));

  const selectedPrice = computed(() => ({
    ...priceData.value,
    badgesList: validatedOffers.value,
    fauxOffer: fauxOffer.value,
    perksPromotions: perksPromotions.value,
    ccfpData: rawData.value.ccfp || {},
    departmentData: rawData.value.department || {},
    flags: {
      ...priceData.value.flags,
      creditFinance: rawData.value.isCCFP || rawData.value.klarnaEligible,
      klarna: rawData.value.klarnaEligible,
      isConsolidatedMaster: rawData.value.isConsolidatedMaster,
    },
    id: toValue(id),
    nonGiftPromotionMessage: rawData.value.pricing?.nonGiftPromotionMessage,
  }));

  return {
    selectedPrice,
  };
}

export default usePrice;

export { usePrice, useInitPrice };
